import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const PERIODS = [
  { key: 'mtd', label: 'This Month' },
  { key: '3m', label: 'Last 3 Months' },
  { key: '6m', label: 'Last 6 Months' },
  { key: 'ytd', label: 'Year to Date' },
  { key: 'yoy', label: 'Last 12 Months' }
];

const getPeriodDates = (period) => {
  const now = new Date();
  let start;
  switch (period) {
    case 'mtd': start = new Date(now.getFullYear(), now.getMonth(), 1); break;
    case '3m': start = new Date(now); start.setMonth(start.getMonth() - 3); break;
    case '6m': start = new Date(now); start.setMonth(start.getMonth() - 6); break;
    case 'ytd': start = new Date(now.getFullYear(), 0, 1); break;
    case 'yoy': start = new Date(now); start.setFullYear(start.getFullYear() - 1); break;
    default: start = new Date(now.getFullYear(), now.getMonth(), 1);
  }
  return { start, end: now };
};

const getMonthlyBreakdown = (transactions) => {
  const months = {};
  transactions.forEach(t => {
    const d = new Date(t.created_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-CA', { month: 'short', year: 'numeric' });
    if (!months[key]) months[key] = { key, label, gross: 0, coach: 0, hma: 0, gst: 0, count: 0, refunds: 0 };

    if (t.status === 'refunded') {
      // Subtract refunded amounts
      months[key].gross -= Number(t.gross_amount || 0);
      months[key].coach -= Number(t.coach_amount || 0);
      months[key].hma -= Number(t.hma_amount || 0);
      months[key].gst -= Number(t.gst_amount || 0);
      months[key].refunds += 1;
    } else {
      months[key].gross += Number(t.gross_amount || 0);
      months[key].coach += Number(t.coach_amount || 0);
      months[key].hma += Number(t.hma_amount || 0);
      months[key].gst += Number(t.gst_amount || 0);
      months[key].count += 1;
    }
  });
  return Object.values(months).sort((a, b) => a.key.localeCompare(b.key));
};

export default function Reporting() {
  const { profile } = useAuth();
  const [period, setPeriod] = useState('mtd');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);

  const fetchData = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    const { start, end } = getPeriodDates(period);

    // Fetch both completed AND refunded so we can subtract refunds properly
    const { data } = await supabase
      .from('transactions')
      .select('*')
      .eq('coach_id', profile.id)
      .in('status', ['completed', 'refunded'])
      .gte('created_at', start.toISOString())
      .lte('created_at', end.toISOString())
      .order('created_at', { ascending: false });

    const txns = data || [];
    setTransactions(txns);

    // Split into completed and refunded
    const completed = txns.filter(t => t.status === 'completed');
    const refunded = txns.filter(t => t.status === 'refunded');

    // Net figures = completed - refunded
    const gross = completed.reduce((s, t) => s + Number(t.gross_amount || 0), 0)
                - refunded.reduce((s, t) => s + Number(t.gross_amount || 0), 0);
    const coach = completed.reduce((s, t) => s + Number(t.coach_amount || 0), 0)
                - refunded.reduce((s, t) => s + Number(t.coach_amount || 0), 0);
    const hma   = completed.reduce((s, t) => s + Number(t.hma_amount || 0), 0)
                - refunded.reduce((s, t) => s + Number(t.hma_amount || 0), 0);
    const mentor = completed.reduce((s, t) => s + Number(t.mentor_amount || 0), 0)
                 - refunded.reduce((s, t) => s + Number(t.mentor_amount || 0), 0);
    const gst   = completed.reduce((s, t) => s + Number(t.gst_amount || 0), 0)
                - refunded.reduce((s, t) => s + Number(t.gst_amount || 0), 0);

    const uniqueClients = new Set(completed.filter(t => t.client_id).map(t => t.client_id)).size;
    const avg = completed.length > 0
      ? completed.reduce((s, t) => s + Number(t.gross_amount || 0), 0) / completed.length
      : 0;

    setStats({
      gross, coach, hma, mentor, gst,
      count: completed.length,
      refundCount: refunded.length,
      uniqueClients,
      avg
    });
    setMonthlyData(getMonthlyBreakdown(txns));
    setLoading(false);
  }, [profile, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2 })}`;

  const exportCSV = () => {
    const completed = transactions.filter(t => t.status === 'completed');
    const headers = ['Date', 'Description', 'Status', 'Gross', 'GST', 'Subtotal', 'HMA Cut', 'Mentor Cut', 'Your Revenue'];
    const rows = transactions.map(t => [
      new Date(t.created_at).toLocaleDateString('en-CA'),
      t.description || 'Payment',
      t.status,
      Number(t.gross_amount || 0).toFixed(2),
      Number(t.gst_amount || 0).toFixed(2),
      Number(t.subtotal_amount || t.gross_amount || 0).toFixed(2),
      Number(t.hma_amount || 0).toFixed(2),
      Number(t.mentor_amount || 0).toFixed(2),
      Number(t.coach_amount || 0).toFixed(2)
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hma-report-${period}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const maxGross = Math.max(...monthlyData.map(m => m.gross), 1);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 32, marginBottom: 8 }}>Reporting</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Revenue insights for your coaching business</p>
        </div>
        <button className="btn btn-secondary" onClick={exportCSV} disabled={loading || !transactions.length}>
          ↓ Export CSV
        </button>
      </div>

      {/* Period selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
        {PERIODS.map(p => (
          <button key={p.key} onClick={() => setPeriod(p.key)}
            className={period === p.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)' }}>Loading...</div>
      ) : (
        <>
          {/* Refund notice */}
          {stats?.refundCount > 0 && (
            <div className="alert alert-error" style={{ marginBottom: 20 }}>
              {stats.refundCount} refund{stats.refundCount > 1 ? 's' : ''} in this period — figures below are net of refunds.
            </div>
          )}

          {/* Key metrics */}
          <div className="stat-grid" style={{ marginBottom: 28 }}>
            {[
              { label: 'Gross Revenue', value: fmt(stats?.gross), sub: 'net of refunds', icon: '◇' },
              { label: 'My Revenue', value: fmt(stats?.coach), sub: 'after HMA split', icon: '✦', highlight: true },
              { label: 'HMA Cut', value: fmt(stats?.hma), sub: 'platform fee', icon: '◆' },
              { label: 'GST Collected', value: fmt(stats?.gst), sub: 'for remittance', icon: '◎' },
              { label: 'Transactions', value: stats?.count, sub: `${stats?.refundCount > 0 ? `${stats.refundCount} refunded` : 'payments received'}`, icon: '◈' },
              { label: 'Active Clients', value: stats?.uniqueClients, sub: 'paid this period', icon: '◉' },
              { label: 'Avg Transaction', value: fmt(stats?.avg), sub: 'per payment', icon: '◃' },
              ...(stats?.mentor > 0 ? [{ label: 'Mentor Paid Out', value: fmt(stats?.mentor), sub: 'to your mentor', icon: '◎' }] : [])
            ].map((s, i) => (
              <div key={i} className="stat-card" style={s.highlight ? { borderColor: 'rgba(201,169,110,0.4)', background: 'rgba(201,169,110,0.05)' } : {}}>
                <div className="stat-label">{s.icon} {s.label}</div>
                <div className="stat-value" style={s.highlight ? { color: 'var(--accent)' } : {}}>{s.value ?? '—'}</div>
                <div className="stat-sub">{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Monthly chart */}
          {monthlyData.length > 1 && (
            <div className="card" style={{ marginBottom: 28 }}>
              <h3 style={{ fontSize: 18, marginBottom: 24 }}>Revenue by Month</h3>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 180, padding: '0 8px' }}>
                {monthlyData.map((m, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>{fmt(m.gross)}</div>
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                      <div style={{
                        height: Math.max(4, (Math.max(m.coach, 0) / maxGross) * 140),
                        background: 'var(--accent)', borderRadius: '3px 3px 0 0'
                      }} title={`Your revenue: ${fmt(m.coach)}`} />
                      <div style={{
                        height: Math.max(2, (Math.max(m.hma, 0) / maxGross) * 140),
                        background: 'rgba(201,169,110,0.3)', borderRadius: '0 0 3px 3px'
                      }} title={`HMA cut: ${fmt(m.hma)}`} />
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center', whiteSpace: 'nowrap' }}>{m.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--accent)', borderRadius: 2, marginRight: 4 }} />Your revenue</span>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'rgba(201,169,110,0.3)', borderRadius: 2, marginRight: 4 }} />HMA cut</span>
              </div>
            </div>
          )}

          {/* Monthly breakdown table */}
          {monthlyData.length > 0 && (
            <div className="card" style={{ marginBottom: 28, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px 12px' }}>
                <h3 style={{ fontSize: 18 }}>Monthly Breakdown</h3>
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Gross</th>
                    <th>GST</th>
                    <th>HMA Cut</th>
                    <th>Your Revenue</th>
                    <th>Payments</th>
                    <th>Refunds</th>
                  </tr>
                </thead>
                <tbody>
                  {[...monthlyData].reverse().map((m, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{m.label}</td>
                      <td>{fmt(m.gross)}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{fmt(m.gst)}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{fmt(m.hma)}</td>
                      <td style={{ color: 'var(--success)', fontWeight: 600 }}>{fmt(m.coach)}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{m.count}</td>
                      <td style={{ color: m.refunds > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
                        {m.refunds > 0 ? `-${m.refunds}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {transactions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)', border: '1px dashed var(--border)', borderRadius: 12 }}>
              No transactions found for this period.
            </div>
          )}
        </>
      )}
    </div>
  );
}
